import React from 'react';
import * as R from 'ramda';

import {
  BooleanField,
  FunctionField,
  Labeled,
  NumberField,
  SelectField,
  TextField,
  UrlField,
  useQuery,
} from 'react-admin';
import { authServer } from 'auth/keycloak';
import { Box, Grid, Typography } from '@material-ui/core';

import {
  campuses,
  campusesAll,
  cohortTimezones,
  formats,
  languages,
  partTimeDays,
  partTimeHours,
  tracks,
} from 'data';
import { DateField } from 'resources/common';
import { hasNewStudentPortal } from 'utils/hasNewStudentPortal';
import AdditionalInformationShow from '../components/AdditionalInformationShow/AdditionalInformationShow';
import ContentAccessButton from 'resources/common/ContentAccessButton';
import PreviewButton from 'resources/common/PreviewButton';
import CoursePublicationChip from 'resources/common/CoursePublicationChip';
import CourseLastPublishedField from 'resources/common/CourseLastPublishedField';
import CourseOpenEdxIdField from 'resources/common/CourseOpenEdxIdField';
import useCoursePublicationWatch from 'resources/common/useCoursePublicationWatch';

const InformationTab = ({ record, resource }) => {
  const { liveRecord, onGithubOpen } = useCoursePublicationWatch({
    record,
    resource,
  });
  const courseWelcomeEmailTemplateId =
    liveRecord?.course?.email_templates?.welcome_email_template_id;
  const careerWelcomeEmailTemplateId =
    liveRecord?.career_services?.email_templates?.welcome_email_template_id;

  const { data: emailTemplates, loading: emailTemplatesLoading } = useQuery({
    type: 'getList',
    resource: 'emails',
    payload: {
      pagination: {
        page: 1,
        perPage: 100,
      },
      sort: {
        field: 'name',
        order: 'ASC',
      },
      filter: {
        id: {
          $in: [courseWelcomeEmailTemplateId, careerWelcomeEmailTemplateId],
        },
      },
    },
  });

  const getEmailTemplateNames = (searchedFamilies) =>
    R.compose(
      R.ifElse(
        R.has('name'),
        (template) =>
          `${template.name}${
            template.language ? ` -  ${template.language.toUpperCase()}` : ''
          }`,
        R.always('--'),
      ),
      R.last,
      R.filter(R.propSatisfies(R.includes(R.__, searchedFamilies), 'family')),
    )(emailTemplates);

  const isBerlinOrGermanCohort =
    liveRecord.campus === 'ber' ||
    (liveRecord.timezone === 'Europe/Madrid' && liveRecord.language === 'en') ||
    liveRecord.language === 'de';
  const cohortId = liveRecord?.id || liveRecord?._id;
  const usesNewStudentPortal = hasNewStudentPortal(cohortId, liveRecord?.start_date);
  const { data: cohortSettings } = useQuery({
    type: 'getOne',
    resource: 'cohort_settings',
    payload: {
      id: cohortId,
    },
  });
  const multilanguageRecord = usesNewStudentPortal
    ? { multilanguage_enabled: Boolean(cohortSettings?.multilanguageEnabled) }
    : R.hasPath(['feature_flags', 'multilanguage_enabled'], liveRecord)
      ? liveRecord
      : { feature_flags: { multilanguage_enabled: false } };

  return (
    <Box p="16px">
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Cohort
          </Typography>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Track">
            <SelectField source="track" choices={tracks} record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Format">
            <SelectField source="format" choices={formats} record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box flexDirection="row">
            <Box>
              <Labeled label="Campus">
                <SelectField
                  source="campus"
                  choices={campuses}
                  record={liveRecord}
                />
              </Labeled>
            </Box>
            <Box>
              {R.has('timezone', liveRecord) && (
                <Labeled label="Timezone">
                  <SelectField
                    source="timezone"
                    choices={cohortTimezones}
                    record={liveRecord}
                  />
                </Labeled>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Language">
            <SelectField
              source="language"
              choices={languages}
              record={liveRecord}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12}>
          <Box mt="2em">
            <Typography variant="h5" gutterBottom>
              Dates
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Start Date">
            <DateField source="start_date" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="End Date">
            <DateField source="end_date" record={liveRecord} />
          </Labeled>
        </Grid>
        {liveRecord.format !== 'other' && (
          <Grid item xs={12} md={6}>
            <Labeled label="Career Week Start Date">
              <DateField source="hiring_fair.start_date" record={liveRecord} />
            </Labeled>
          </Grid>
        )}
        {liveRecord.format !== 'other' && (
          <Grid item xs={12} md={6}>
            <Labeled label="Career Week End Date">
              <DateField source="hiring_fair.end_date" record={liveRecord} />
            </Labeled>
          </Grid>
        )}
        {R.propEq('format', 'pt', liveRecord) && (
          <Grid item xs={12} md={3}>
            <Labeled label="Part Time Days">
              <SelectField
                source="timetable.days"
                choices={partTimeDays}
                record={liveRecord}
              />
            </Labeled>
          </Grid>
        )}
        {R.propEq('format', 'pt', liveRecord) && (
          <Grid item xs={12} md={3}>
            <Labeled label="Weekday Start Time">
              <SelectField
                source="timetable.time.0.start_time"
                choices={partTimeHours}
                optionText="id"
                record={liveRecord}
              />
            </Labeled>
          </Grid>
        )}
        {R.propEq('format', 'pt', liveRecord) && (
          <Grid item xs={12} md={2}>
            <Labeled label="Weekday End Time">
              <SelectField
                source="timetable.time.0.end_time"
                choices={partTimeHours}
                optionText="id"
                record={liveRecord}
              />
            </Labeled>
          </Grid>
        )}
        {R.propEq('format', 'pt', liveRecord) && (
          <Grid item xs={12} md={2}>
            <Labeled label="Weekend Start Time">
              <SelectField
                source="timetable.time.1.start_time"
                choices={partTimeHours}
                optionText="id"
                record={liveRecord}
              />
            </Labeled>
          </Grid>
        )}
        {R.propEq('format', 'pt', liveRecord) && (
          <Grid item xs={12} md={2}>
            <Labeled label="Weekend End Time">
              <SelectField
                source="timetable.time.1.end_time"
                choices={partTimeHours}
                optionText="id"
                record={liveRecord}
              />
            </Labeled>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box mt="2em">
            <Typography variant="h5" gutterBottom>
              Staff
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="PM">
            <FunctionField
              source="staff.pm"
              record={liveRecord}
              render={R.pipe(
                R.pathOr({}, ['staff', 'pm']),
                R.props(['first_name', 'last_name']),
                R.join(' '),
              )}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="PM Assistant">
            <FunctionField
              source="staff.pm_assistant"
              record={liveRecord}
              render={R.pipe(
                R.pathOr({}, ['staff', 'pm_assistant']),
                R.props(['first_name', 'last_name']),
                R.join(' '),
                R.trim,
              )}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Student Success Manager">
            <FunctionField
              source="staff.om"
              record={liveRecord}
              render={R.pipe(
                R.pathOr({}, ['staff', 'om']),
                R.props(['first_name', 'last_name']),
                R.join(' '),
              )}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Student Success Assistant">
            <FunctionField
              source="staff.om_assistant"
              record={liveRecord}
              render={R.pipe(
                R.pathOr({}, ['staff', 'om_assistant']),
                R.props(['first_name', 'last_name']),
                R.join(' '),
                R.trim,
              )}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Teachers">
            <FunctionField
              record={liveRecord}
              render={R.pipe(
                R.pathOr([], ['staff', 'teachers']),
                R.map(
                  R.pipe(
                    R.props(['first_name', 'last_name']),
                    R.join(' '),
                    R.trim,
                  ),
                ),
                R.join(', '),
                R.trim,
              )}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Number Of TAs">
            <TextField source="staff.ta_amount" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12}>
          <Box mt="2em" mb="10px">
            <Typography variant="h5" gutterBottom>
              Courses
            </Typography>
          </Box>
        </Grid>
        <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
          {R.has('prework', liveRecord) && (
            <Box>
              <Box mb="10px" display="flex" alignItems="center">
                <Typography variant="h6" component="span">
                  {R.has('preparation', liveRecord) ? 'Prework (M0)' : 'Prework'}
                </Typography>
                <CoursePublicationChip
                  status={R.path(['prework', 'publication', 'status'], liveRecord)}
                  loading={!R.path(['prework', 'publication'], liveRecord)}
                />
              </Box>
              <Box>
                <Labeled label="Student Portal Title">
                  <TextField source="prework.title" record={liveRecord} />
                </Labeled>
              </Box>
              <CourseOpenEdxIdField record={liveRecord} source="prework" />
              <CourseLastPublishedField record={liveRecord} source="prework" />
              <Box mt="10px">
                <ContentAccessButton record={liveRecord} source="prework" onGithubOpen={onGithubOpen} />
              </Box>
              <Box mt="10px">
                <PreviewButton record={liveRecord} source="prework" />
              </Box>
            </Box>
          )}
        </Box>
        <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
          <Box mb="10px" display="flex" alignItems="center">
            <Typography variant="h6" component="span">
              Main Course
            </Typography>
            <CoursePublicationChip
              status={R.path(['course', 'publication', 'status'], liveRecord)}
              loading={!R.path(['course', 'publication'], liveRecord)}
            />
          </Box>
          <Box>
            <Labeled label="Student Portal Title">
              <TextField source="course.title" record={liveRecord} />
            </Labeled>
          </Box>
          <CourseOpenEdxIdField record={liveRecord} source="course" />
          <CourseLastPublishedField record={liveRecord} source="course" />
          <Box mt="10px">
            <ContentAccessButton record={liveRecord} source="course" onGithubOpen={onGithubOpen} />
          </Box>
          <Box mt="10px">
            <PreviewButton record={liveRecord} source="course" />
          </Box>
        </Box>
        <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
          {R.has('career_services', liveRecord) && (
            <Box>
              <Box mb="10px" display="flex" alignItems="center">
                <Typography variant="h6" component="span">
                  Career Services
                </Typography>
                <CoursePublicationChip
                  status={R.path(
                    ['career_services', 'publication', 'status'],
                    liveRecord,
                  )}
                  loading={
                    !R.path(['career_services', 'publication'], liveRecord)
                  }
                />
              </Box>
              <Box>
                <Labeled label="Student Portal Title">
                  <TextField source="career_services.title" record={liveRecord} />
                </Labeled>
              </Box>
              <CourseOpenEdxIdField record={liveRecord} source="career_services" />
              <CourseLastPublishedField
                record={liveRecord}
                source="career_services"
              />
              <Box mt="10px">
                <ContentAccessButton record={liveRecord} source="career_services" onGithubOpen={onGithubOpen} />
              </Box>
              <Box mt="10px">
                <PreviewButton record={liveRecord} source="career_services" />
              </Box>
            </Box>
          )}
        </Box>
        {authServer.isRoles(['admin', 'gm', 'pm', 'outcomes', 'teacher']) &&
          !emailTemplatesLoading && (
            <>
              <Grid item xs={12}>
                <Box mt="2em">
                  <Typography variant="h5" gutterBottom>
                    Cohort specifics
                    {usesNewStudentPortal && (
                      <Box component="sup" ml={1}>
                        <Box
                          component="span"
                          px={1}
                          py={0.25}
                          borderRadius="12px"
                          border={1}
                          borderColor="primary.main"
                          bgcolor="primary.main"
                          color="primary.contrastText"
                          fontSize="0.62em"
                          fontWeight={700}
                          letterSpacing="0.04em"
                          lineHeight={1.15}
                          textTransform="uppercase"
                        >
                          New Student Portal
                        </Box>
                      </Box>
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid flex={1} flexDirection="row" mr={{ md: '2em' }}>
                <Grid item xs={12} md={6}>
                  <Labeled label="Course Welcome Email">
                    <Typography>
                      {getEmailTemplateNames([
                        'course_welcome',
                        'course_welcome_preparation',
                      ])}
                    </Typography>
                  </Labeled>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Labeled label="Career Services Welcome Email">
                    <Typography>
                      {getEmailTemplateNames('career_welcome')}
                    </Typography>
                  </Labeled>
                </Grid>
                <Grid item>
                  <Box display="flex" alignItems="flex-start">
                    <Labeled label="Zoom URL">
                      {liveRecord.zoom_url ? (
                        <UrlField source="zoom_url" record={liveRecord} />
                      ) : (
                        <Typography>--</Typography>
                      )}
                    </Labeled>
                    <Box ml={3}>
                      <Labeled label="Automatic Attendance enabled">
                        <BooleanField
                          record={
                            R.hasPath(
                              ['feature_flags', 'automatic_attendance_enabled'],
                              liveRecord,
                            )
                              ? liveRecord
                              : {
                                  feature_flags: {
                                    automatic_attendance_enabled: false,
                                  },
                                }
                          }
                          source="feature_flags.automatic_attendance_enabled"
                        />
                      </Labeled>
                    </Box>
                  </Box>
                </Grid>
                <Grid item>
                  <Labeled label="Link to FAQ">
                    {liveRecord.faq_url ? (
                      <UrlField source="faq_url" record={liveRecord} />
                    ) : (
                      <Typography>--</Typography>
                    )}
                  </Labeled>
                </Grid>
                <Grid item>
                  <Labeled label="Slack URL">
                    {liveRecord.slack_url ? (
                      <UrlField source="slack_url" record={liveRecord} />
                    ) : (
                      <Typography>--</Typography>
                    )}
                  </Labeled>
                </Grid>
                <Grid item>
                  <Labeled label="Onboarding Video">
                    {liveRecord.presentation_video ? (
                      <UrlField source="presentation_video" record={liveRecord} />
                    ) : (
                      <Typography>--</Typography>
                    )}
                  </Labeled>
                </Grid>

                {R.hasPath(
                  ['external_ids', 'accredible_design_id'],
                  liveRecord,
                ) && (
                  <Grid item>
                    <Labeled label="Accredible design ID">
                      {liveRecord.external_ids?.accredible_design_id ? (
                        <TextField
                          source="external_ids.accredible_design_id"
                          record={liveRecord}
                        />
                      ) : (
                        <Typography>--</Typography>
                      )}
                    </Labeled>
                  </Grid>
                )}
                <Grid item xs={12} md={10}>
                  <Labeled label="Multilanguage enabled">
                    <BooleanField
                      record={multilanguageRecord}
                      source={
                        usesNewStudentPortal
                          ? 'multilanguage_enabled'
                          : 'feature_flags.multilanguage_enabled'
                      }
                    />
                  </Labeled>
                </Grid>
              </Grid>
            </>
          )}
        <Grid item xs={12}>
          <Box mt="2em">
            <Typography variant="h5" gutterBottom>
              Price
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Price">
            <NumberField
              source="price.amount"
              options={{
                style: 'currency',
                currency: R.path(['price', 'currency'], liveRecord),
              }}
              record={liveRecord}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Discount">
            <NumberField
              source="price.discount"
              options={{
                style: 'currency',
                currency: R.path(['price', 'currency'], liveRecord),
              }}
              record={liveRecord}
            />
          </Labeled>
        </Grid>
        {liveRecord.offerings && (
          <>
            <Grid item xs={12}>
              <Box mt="2em">
                <Typography variant="h5" gutterBottom>
                  Offerings
                </Typography>
              </Box>
            </Grid>
            {liveRecord.offerings.map((offering) => {
              return (
                <>
                  <Grid item xs={12} md={3}>
                    <Labeled label="Campus">
                      <SelectField
                        source="campus"
                        choices={campusesAll}
                        record={offering}
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Labeled label="Price">
                      <NumberField
                        source="price.amount"
                        options={{
                          style: 'currency',
                          currency: R.path(['price', 'currency'], offering),
                        }}
                        record={offering}
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Labeled label="Discount">
                      <NumberField
                        source="price.discount"
                        options={{
                          style: 'currency',
                          currency: R.path(['price', 'currency'], offering),
                        }}
                        record={offering}
                      />
                    </Labeled>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Labeled label="Target">
                      <NumberField source="target" record={offering} />
                    </Labeled>
                  </Grid>
                </>
              );
            })}
          </>
        )}
        <Grid item xs={12}>
          <Box mt="2em">
            <Typography variant="h5" gutterBottom>
              Target
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Target">
            <NumberField source="target" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12}>
          <Box mt="2em">
            <Typography variant="h5" gutterBottom>
              Web Status
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Shown On Web">
            <BooleanField source="active" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Shown As Sold Out">
            <BooleanField source="sold_out" record={liveRecord} />
          </Labeled>
        </Grid>
        {isBerlinOrGermanCohort && (
          <Grid item xs={12}>
            <Box mt="2em">
              <AdditionalInformationShow record={liveRecord} />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default InformationTab;
